import { Button, Loader, Textarea } from '@mantine/core'
import { IconAlertTriangle, IconArrowsExchange, IconBan, IconUsers } from '@tabler/icons-react'
import { useState, type ComponentProps, type ReactNode } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { cn } from '@/utils/cn'
import {
  useDecideDuplicateReview,
  useDuplicateReview,
  useMergePatients,
  type DuplicateReviewComparison,
  type DuplicateReviewDecision,
  type PatientMergeInput,
} from '@/hooks/usePatientMerge'
import { DemographicsTable } from './components/DemographicsTable'
import { MatchStrengthPill } from './components/MatchStrengthPill'
import { MergeConfirmModal, type MergeRequest } from './components/MergeConfirmModal'
import { KeptChartLink, MergeProgress } from './components/MergeProgress'
import { RecordColumn, type RecordRole } from './components/RecordColumn'
import { type ComparisonRecord, type RecordIndex } from './components/reviewFormat'

const QUEUE_PATH = '/patients/duplicates'

/** What the open confirmation shows and sends — frozen when it opens. */
type Confirmation = Pick<
  ComponentProps<typeof MergeConfirmModal>,
  'kept' | 'merged' | 'mergedRecordCounts' | 'request'
>

const CARD_CLASS =
  'rounded-[18px] border border-[#E7E9EC] bg-white px-6 py-5 shadow-[0_1px_3px_rgba(16,24,40,.05),0_12px_30px_rgba(16,24,40,.05)]'
const SECTION_TITLE_CLASS = 'mb-3 text-[11px] font-semibold tracking-wider text-[#8A94A6] uppercase'

const CLOSED_STATUS_TEXT: Partial<Record<DuplicateReviewComparison['review']['status'], string>> = {
  'not-duplicate': 'Marked as not a duplicate',
  cancelled: 'Closed — one of these records was merged in another review',
}

const COUNT_ROWS: { key: keyof ComparisonRecord['counts']; label: string }[] = [
  { key: 'appointmentsUpcoming', label: 'Upcoming appointments' },
  { key: 'appointmentsPast', label: 'Past appointments' },
  { key: 'encounters', label: 'Encounters' },
  { key: 'documents', label: 'Documents' },
  { key: 'orders', label: 'Orders' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'messages', label: 'Messages' },
  { key: 'careQueueTasks', label: 'Care Queue tasks' },
  { key: 'billingCases', label: 'Billing cases' },
  { key: 'importedHgRecords', label: 'Imported TEFCA records' },
  { key: 'doseSpotPrescriptions', label: 'DoseSpot prescriptions' },
]

function CountsTable({ records }: { records: DuplicateReviewComparison['records'] }) {
  return (
    <table
      aria-label="Associated records"
      className="w-full table-fixed border-collapse text-[13px]"
    >
      <thead>
        <tr className="border-b border-[#EAECEF] text-left text-[11px] font-semibold tracking-wider text-[#8A94A6] uppercase">
          <th className="w-[200px] py-2 pr-3">Record type</th>
          {records.map((record, index) => (
            <th key={index} className="py-2 pr-3">
              {record.summary.name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {COUNT_ROWS.map(({ key, label }) => (
          <tr key={key} className="border-b border-[#F0F1F3] last:border-b-0">
            <td className="py-2 pr-3 text-[#8A94A6]">{label}</td>
            {records.map((record, index) => (
              <td
                key={index}
                className={`py-2 pr-3 tabular-nums ${record.counts[key] ? 'text-[#1F2733]' : 'text-[#B4B9C0]'}`}
              >
                {record.counts[key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className={CARD_CLASS}>
    <h2 className={SECTION_TITLE_CLASS}>{title}</h2>
    {children}
  </div>
)

/**
 * One duplicate review, side by side (PD-1986): which record to keep (the middleware's
 * recommendation by default), the evidence behind it, demographics, counts and conflicts, and the
 * three decisions. After a merge starts, the page follows it (`?merge=<id>` survives a reload).
 * No business logic here: the recommendation, conflicts and blockers all come from the server,
 * which re-validates the merge.
 */
export function DuplicateReviewPage() {
  const { reviewId = '' } = useParams()
  // Keyed so that moving to another review starts over (kept record, reason).
  return <ReviewPage key={reviewId} reviewId={reviewId} />
}

function ReviewPage({ reviewId }: { reviewId: string }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const mergeId = searchParams.get('merge') ?? undefined
  const [chosenKeepId, setChosenKeepId] = useState<string>()
  // While a swap refetches, the previous comparison stays on screen (`isPlaceholderData`).
  const query = useDuplicateReview(reviewId, chosenKeepId)
  const { data } = query
  // Owned here, not by the modal: the switch to the progress view must happen even if the modal
  // has gone by the time the server answers.
  const merge = useMergePatients({
    onSuccess: (result) => setSearchParams({ merge: result.mergeId }),
  })
  // A review opened from the queue follows its merge: the one that completed it, or one still
  // unfinished (the server names it only then), so an interrupted merge can be resumed (R45).
  const followedMergeId = mergeId ?? data?.review.mergeId

  return (
    <div className="box-border min-h-[calc(100vh-64px)] bg-[#F6F7F9] p-4 text-[#1F2733]">
      <div className="mx-auto flex max-w-[1180px] flex-col gap-4">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <Link
            to={QUEUE_PATH}
            className="text-[13px] font-semibold !text-[#6B7280] no-underline hover:underline"
          >
            ← Duplicate review
          </Link>
          <div className="flex items-center gap-2.5">
            <IconUsers size={22} className="text-[#E5533D]" stroke={2} />
            <h1 className="text-xl font-bold tracking-tight">Possible duplicate</h1>
          </div>
          {data && (
            <div className="flex items-center gap-2 text-[13px] text-[#4A5160]">
              <MatchStrengthPill strength={data.review.strength} />
              <span>{data.review.reasonText}</span>
            </div>
          )}
        </div>

        {followedMergeId ? (
          <MergeProgress
            mergeId={followedMergeId}
            // A saved `?merge=` link outlives a Restore, which reopened this review.
            onBackToReview={
              mergeId
                ? () => {
                    setSearchParams({})
                    void query.refetch()
                  }
                : undefined
            }
          />
        ) : data ? (
          <ReviewBody
            comparison={data}
            chosenKeepId={chosenKeepId}
            onKeep={setChosenKeepId}
            refreshing={query.isPlaceholderData}
            onConfirmMerge={(input) => merge.mutate(input)}
            merging={merge.isPending}
          />
        ) : query.isError ? (
          <div className={`${CARD_CLASS} flex flex-col items-center gap-3`}>
            <p className="text-sm font-medium text-[#6B7280]">Couldn&apos;t load this review.</p>
            <Button variant="default" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <div className="flex justify-center py-10">
            <Loader />
          </div>
        )}
      </div>
    </div>
  )
}

function ReviewBody({
  comparison,
  chosenKeepId,
  onKeep,
  refreshing,
  onConfirmMerge,
  merging,
}: {
  comparison: DuplicateReviewComparison
  chosenKeepId: string | undefined
  onKeep: (patientId: string) => void
  /** The comparison on screen is for the previous kept record; its conflicts are stale. */
  refreshing: boolean
  onConfirmMerge: (input: PatientMergeInput) => void
  /** The merge request is in flight. */
  merging: boolean
}) {
  const navigate = useNavigate()
  const { review, records, differingFields, recommendation, conflicts, blockers } = comparison
  const [reason, setReason] = useState('')
  // Frozen when the confirmation opens: a background refetch that flips the recommendation must
  // not swap the merge direction under a ticked box and a typed "Merge".
  const [confirmation, setConfirmation] = useState<Confirmation>()
  const decide = useDecideDuplicateReview({ onSuccess: () => void navigate(QUEUE_PATH) })

  const open = review.status === 'awaiting' || review.status === 'later'
  const keepId = chosenKeepId ?? recommendation.recommendedPatientId ?? undefined
  const found = records.findIndex((record) => record.summary.patientId === keepId)
  const keepIndex = found === 0 || found === 1 ? (found as RecordIndex) : undefined
  const roleOf = (index: number): RecordRole =>
    keepIndex === undefined ? undefined : index === keepIndex ? 'keep' : 'merge'
  // Mirrors the server's rule (it re-checks): conflicting indicators, or keeping a record other
  // than the recommended one, needs a reason.
  const reasonRequired =
    keepIndex !== undefined &&
    (recommendation.conflicting || recommendation.recommendedPatientId !== keepId)
  const decideAs = (decision: DuplicateReviewDecision) =>
    decide.mutate({ reviewId: review.reviewId, decision })

  const mergeDisabled =
    keepIndex === undefined ||
    blockers.length > 0 ||
    refreshing ||
    decide.isPending ||
    (reasonRequired && !reason.trim())

  const keptRecord = keepIndex === undefined ? undefined : records[keepIndex]
  const mergedRecord = keepIndex === undefined ? undefined : records[keepIndex === 0 ? 1 : 0]
  const request: MergeRequest | undefined = keptRecord && {
    reviewId: review.reviewId,
    keepPatientId: keptRecord.summary.patientId,
    ...(reasonRequired && { reason: reason.trim() }),
  }

  // Read-only once decided: a merged review shows the result, others their status.
  const keptAfterMerge = records.find((record) => !record.summary.merged)
  const closedText = CLOSED_STATUS_TEXT[review.status]

  return (
    <>
      {review.status === 'merged' && (
        <div className={cn(CARD_CLASS, 'flex flex-col items-start gap-2 border-[#9BD3B0]')}>
          <h2 className="text-lg font-bold">Records merged</h2>
          {keptAfterMerge && (
            <KeptChartLink
              patientId={keptAfterMerge.summary.patientId}
              name={keptAfterMerge.summary.name}
            />
          )}
        </div>
      )}
      {closedText && (
        <div className={`${CARD_CLASS} flex items-center gap-2 text-sm font-semibold`}>
          <IconBan size={18} className="text-[#8A94A6]" aria-hidden />
          {closedText}
        </div>
      )}

      {open && (
        <div className={`${CARD_CLASS} flex flex-wrap items-center gap-4`}>
          <div className="min-w-0 flex-1">
            <h2 className={SECTION_TITLE_CLASS + ' !mb-1'}>Recommendation</h2>
            <p className="text-sm text-[#1F2733]">{recommendation.explanation}</p>
          </div>
          {keepIndex !== undefined && mergedRecord && (
            <Button
              variant="default"
              leftSection={<IconArrowsExchange size={16} />}
              onClick={() => onKeep(mergedRecord.summary.patientId)}
            >
              Keep the other record instead
            </Button>
          )}
        </div>
      )}

      {open && blockers.length > 0 && (
        <div
          role="alert"
          className="flex gap-2 rounded-[14px] border border-[#F5C2BA] bg-[#FDECE8] px-5 py-4 text-sm text-[#8F1D1D]"
        >
          <IconBan size={18} className="mt-0.5 shrink-0" aria-hidden />
          <ul className="m-0 list-none p-0">
            {blockers.map((blocker, index) => (
              <li key={index}>{blocker}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {records.map((record, index) => (
          <RecordColumn
            key={record.summary.patientId}
            record={record}
            role={open ? roleOf(index) : undefined}
            onKeep={open ? () => onKeep(record.summary.patientId) : undefined}
          />
        ))}
      </div>

      <Section title="Demographics">
        <DemographicsTable records={records} differingFields={differingFields} />
      </Section>

      <Section title="Associated records">
        <CountsTable records={records} />
      </Section>

      {open && conflicts.length > 0 && (
        <div className="flex gap-2 rounded-[14px] border border-[#F2D3A1] bg-[#FFF8EC] px-5 py-4 text-sm text-[#7A4B00]">
          <IconAlertTriangle size={18} className="mt-0.5 shrink-0" aria-hidden />
          <div>
            <p className="mb-1 font-semibold">Before you merge</p>
            <ul className="m-0 flex list-disc flex-col gap-1 pl-4">
              {conflicts.map((conflict, index) => (
                <li key={`${conflict.kind}-${index}`}>{conflict.message}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {open && (
        <div className={`${CARD_CLASS} flex flex-col gap-4`}>
          {reasonRequired && (
            <Textarea
              label="Reason"
              description={
                recommendation.conflicting
                  ? 'The indicators conflict — say why this record is the one to keep.'
                  : 'You are keeping the record that was not recommended — say why.'
              }
              required
              autosize
              minRows={2}
              value={reason}
              onChange={(event) => setReason(event.currentTarget.value)}
            />
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="default"
              loading={decide.isPending && decide.variables?.decision === 'not-duplicate'}
              disabled={decide.isPending}
              onClick={() => decideAs('not-duplicate')}
            >
              Not a duplicate
            </Button>
            {review.status === 'awaiting' && (
              <Button
                variant="default"
                loading={decide.isPending && decide.variables?.decision === 'review-later'}
                disabled={decide.isPending}
                onClick={() => decideAs('review-later')}
              >
                Review later
              </Button>
            )}
            <Button
              color="red"
              disabled={mergeDisabled}
              loading={merging}
              onClick={() =>
                keptRecord &&
                mergedRecord &&
                request &&
                setConfirmation({
                  kept: keptRecord.summary,
                  merged: mergedRecord.summary,
                  mergedRecordCounts: COUNT_ROWS.flatMap(({ key, label }) =>
                    mergedRecord.counts[key] > 0 ? [`${label}: ${mergedRecord.counts[key]}`] : [],
                  ),
                  request,
                })
              }
            >
              Merge records…
            </Button>
          </div>
        </div>
      )}

      {confirmation && (
        <MergeConfirmModal
          opened
          onClose={() => setConfirmation(undefined)}
          onConfirm={onConfirmMerge}
          pending={merging}
          {...confirmation}
        />
      )}
    </>
  )
}
